import SecureMacPolicyLayout, { SecureMacPolicySection } from "@/components/SecureMacPolicyLayout";

export default function SecureMacPrivacy() {
  return (
    <SecureMacPolicyLayout
      eyebrow="SecureShield privacy"
      title="Local checks. Clear account boundaries."
      description="This policy explains how SecureShield and SecureShield Pro handle device checks, accounts, licensing, Network Guard, updates, payments and diagnostics. It supplements the LightningFlow website privacy policy."
      seoTitle="SecureShield Privacy — LightningFlow"
      canonicalPath="/secureshield/privacy"
      updated="28 October 2026"
    >
      <SecureMacPolicySection title="Who we are">
        <p>
          TripleThree Global Pty Ltd trading as LightningFlow (ABN 11 681 334 145), South Australia,
          operates SecureShield and SecureShield Pro. Privacy enquiries:{" "}
          <a className="underline" href="mailto:contact@lightningflow.ai?subject=SecureShield%20privacy">
            contact@lightningflow.ai
          </a>.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Local security checks on macOS">
        <p>
          SecureShield reads security settings from macOS on your device to show what needs attention.
          Guided controls open the right System Settings panes. SecureShield Pro can turn on selected
          built-in protections you choose. Those checks run locally. SecureShield does not upload the
          contents of your files, keystrokes, camera, microphone or screen to LightningFlow as part of
          ordinary checking. SecureShield does not collect telemetry or analytics.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Network Guard (SecureShield Pro for Mac)">
        <p>
          Network Guard decides from connection metadata (such as app identity and host/port when
          supplied by macOS). It never reads traffic content. Local history stays on your Mac, is
          capped, can be cleared in-app, and is never uploaded or exported. Where Smart App Profiles
          are available in preview versions, they configure Network Guard locally on the device.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="SecureShield Standard for Windows">
        <p>
          The Windows edition reads local Windows security settings and observations to explain
          what needs attention. These checks run on your PC. Camera and microphone privacy
          shortcuts open Windows Settings; they do not record camera or microphone content.
        </p>
        <p>
          A LightningFlow account and a separately purchased SecureShield licence are required for
          security assessments in the Windows edition, including the Microsoft Store channel.
          The no-account Mac App Store arrangement does not apply to Windows. Sign-in and
          purchase take place in your browser. During activation, the app exchanges a short-lived
          device-session code, product identity and a random installation identifier with the
          LightningFlow account service over HTTPS. The service returns licence and device records.
          Periodic licence refresh sends the device identifier, refresh credential and product identity.
          These account request bodies do not include local security-check results.
        </p>
        <p>
          The installation identifier, licence material, refresh credential and saved user choices
          are stored in your Windows user profile using Windows Data Protection. The Windows app
          does not ask for your account password or payment-card details. Account, entitlement and
          device records are subject to the retention, access, correction and deletion provisions below.
        </p>
        <p>
          For the Microsoft Store channel, Windows and Microsoft Store manage package updates;
          the app opens the Store updates page. The direct-download Windows channel uses a signed
          release feed and installer verification. This describes separate distribution channels,
          not a promise that a website or Store purchase automatically supplies the other channel's
          entitlement. Microsoft Store, Windows and the browser have their own applicable privacy
          terms and settings.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Mac App Store edition (no LightningFlow account)">
        <p>
          The Mac App Store edition of SecureShield is designed to work without a LightningFlow account
          for ordinary use. Apple processes App Store purchase and identity under Apple&apos;s terms
          and privacy policy. LightningFlow does not receive your full payment-card number from Apple.
          Optional purchase linking sends only Apple&apos;s app-transaction identifier and a short-lived
          linking code; account sign-in happens in your browser.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Mac website editions: account, licensing and devices">
        <p>
          SecureShield for Mac website edition and SecureShield Pro for Mac use a LightningFlow consumer account for purchase, entitlement, device
          activation (up to three devices) and gated downloads. We process email address, account
          identifiers, entitlement status, device activation records, approximate license timestamps
          and security events needed to operate the service and prevent abuse. In the app, activation
          uses browser sign-in; the app exchanges a random installation identifier and device
          credential, then refreshes its licence at most once a day.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Website edition updates">
        <p>
          Updates for the public release are downloaded through your LightningFlow account.
          Preview versions with in-app updates check a signed release feed on lightningflow.ai.
          Those update checks fetch only signed release information. Nothing about your Mac&apos;s
          security state is included.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Payments">
        <p>
          Stripe processes SecureShield website edition and SecureShield Pro card payments under Stripe&apos;s privacy policy. LightningFlow
          stores billing references and purchase state needed for entitlements, refunds, disputes and
          accounting — not your full card number.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Diagnostics you choose to share">
        <p>
          SecureShield can prepare a local support record. It is not sent automatically. You choose
          whether to export or share it, and the product must show what categories it contains before
          any transmission you initiate.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Retention, access, correction and deletion">
        <p>
          Account, entitlement, device and security records are kept only as long as needed to operate
          the service, resolve disputes, prevent abuse and meet legal obligations. You may request
          access, correction or deletion through your account or the{" "}
          <a className="underline" href="/secureshield/account-deletion">account-deletion page</a>.
        </p>
      </SecureMacPolicySection>
      <SecureMacPolicySection title="Security contact">
        <p>
          Report SecureShield security issues to{" "}
          <a className="underline" href="mailto:contact@lightningflow.ai?subject=SecureShield%20security">
            contact@lightningflow.ai
          </a>. Do not include passwords or payment-card details.
        </p>
      </SecureMacPolicySection>
    </SecureMacPolicyLayout>
  );
}
